/**
 * Armazón de la consola.
 *
 * Deliberadamente distinto del shell del ERP: no hay barra lateral de módulos ni
 * paleta de comandos, hay una barra de sistema oscura con las tres secciones y
 * el contexto (organización, empresa activa, usuario). Quien abra esta pestaña
 * tiene que saber, sin leer, que no está en la aplicación de todos los días.
 *
 * La barra de sistema mantiene el mismo color en tema claro y oscuro a propósito:
 * es la única superficie del producto que se comporta así, y eso es justamente lo
 * que la identifica. El contraste del texto sobre ella se verificó en los dos
 * temas (blanco sobre grafito 20 22 24 = 15.9:1).
 */

import clsx from 'clsx';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { AvisoDemo } from '@/components/shell/AvisoDemo';
import { IconArrowRight, IconBuilding, IconMonitor, IconMoon, IconSun } from '@/components/shell/icons';
import { ETIQUETAS_TEMA, useTheme } from '@/components/shell/theme';
import { ToastHost } from '@/components/shell/ToastHost';
import type { CompanyRef, SessionUser, TenantRef } from '@/lib/types';
import { nombreVisible } from '@/panel/acceso';

/**
 * Alternador de tema propio de la consola.
 *
 * No se reusa el del shell porque aquél se pinta con `text-fg-muted`, pensado
 * para una barra clara; sobre la barra de sistema oscura quedaría gris oscuro
 * sobre grafito y no llegaría a AA. Misma lógica, otros colores.
 */
function ToggleTema() {
  const { preference, cycle } = useTheme();
  const Icono = preference === 'light' ? IconSun : preference === 'dark' ? IconMoon : IconMonitor;
  const siguiente: typeof preference = preference === 'light' ? 'dark' : preference === 'dark' ? 'system' : 'light';

  return (
    <button
      type="button"
      onClick={cycle}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-white"
      aria-label={`${ETIQUETAS_TEMA[preference]}. Cambiar a: ${ETIQUETAS_TEMA[siguiente].toLowerCase()}`}
      title={ETIQUETAS_TEMA[preference]}
    >
      <Icono className="h-[1.0625rem] w-[1.0625rem]" />
    </button>
  );
}

export interface SeccionPanel {
  to: string;
  label: string;
  /** Fin exacto: `/panel` no debe quedar activo estando en `/panel/usuarios`. */
  end?: boolean;
}

export const SECCIONES: SeccionPanel[] = [
  { to: '/panel', label: 'Estado del sistema', end: true },
  { to: '/panel/permisos', label: 'Permisos y roles' },
  { to: '/panel/usuarios', label: 'Usuarios' },
];

function Marca() {
  return (
    <span className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="grid h-7 w-7 place-items-center rounded-sm bg-white/15 font-mono text-sm font-bold text-white"
      >
        N
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-semibold text-white">Núcleo</span>
        <span className="block text-2xs uppercase tracking-[0.14em] text-white/60">Consola</span>
      </span>
    </span>
  );
}

export function PanelLayout({
  children,
  user,
  tenant,
  company,
}: {
  children: ReactNode;
  user: SessionUser | null;
  tenant: TenantRef | null;
  company: CompanyRef | null;
}) {
  return (
    <div className="min-h-svh bg-app-subtle">
      <a href="#consola" className="skip-link">
        Saltar al contenido
      </a>

      {/* La cinta de demostracion va tambien aca: quien administra usuarios y
          roles tiene que saber que el padron que esta mirando es inventado. */}
      <AvisoDemo />

      <header className="sticky top-0 z-20 bg-[rgb(20_22_24)] text-white/80">
        <div className="mx-auto flex w-full max-w-[92rem] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 sm:px-6">
          <Marca />

          <div className="ml-auto flex items-center gap-1 text-xs">
            {tenant !== null && (
              <span className="hidden items-center gap-1.5 rounded-sm px-2 py-1 sm:flex">
                <IconBuilding className="h-3.5 w-3.5 text-white/50" />
                <span className="text-white/80">{tenant.name}</span>
              </span>
            )}
            {company !== null && (
              <span className="hidden rounded-sm bg-white/10 px-2 py-1 text-white/80 md:inline">
                {company.name}
              </span>
            )}
            <span className="rounded-sm px-2 py-1 text-white/70" title={user?.email ?? undefined}>
              {nombreVisible(user)}
            </span>
            <ToggleTema />
            {/* La salida. Estaba escrita como un enlace más de la barra y se
                perdía entre el nombre de la organización y el del usuario: se
                entraba al panel y no se veía por dónde volver. Ahora es un
                botón con borde propio —la única superficie enmarcada de la
                barra— y dice a dónde lleva con el nombre de la aplicación. */}
            <a
              href="/"
              className="flex items-center gap-1.5 rounded-md border border-white/30 bg-white/10 px-2.5 py-1.5 font-medium text-white transition-colors hover:border-white/60 hover:bg-white/20 focus-visible:outline-white"
              title="Salir del panel y volver a Núcleo ERP"
            >
              Volver al ERP
              <IconArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        <nav aria-label="Secciones de la consola" className="border-t border-white/10">
          <ul className="mx-auto flex w-full max-w-[92rem] gap-1 overflow-x-auto px-2 sm:px-4">
            {SECCIONES.map((seccion) => (
              <li key={seccion.to}>
                <NavLink
                  to={seccion.to}
                  end={seccion.end === true}
                  className={({ isActive }) =>
                    clsx(
                      'block whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors focus-visible:outline-white',
                      isActive
                        ? 'border-b-white text-white'
                        : 'border-b-transparent text-white/60 hover:border-b-white/30 hover:text-white',
                    )
                  }
                >
                  {seccion.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main id="consola" tabIndex={-1} className="mx-auto w-full max-w-[92rem] px-4 py-5 sm:px-6">
        {/* La consola no dibuja un titulo grande —la marca de la barra ya dice
            donde estas y el espacio vertical rinde mas en datos— pero la pagina
            necesita un H1 igual: sin el, un lector de pantalla entra a un
            documento que arranca en H2 y no sabe de que trata. */}
        <h1 className="sr-only">Consola de administración de Núcleo</h1>
        {children}
      </main>

      {/* Los avisos del ERP viven en el shell; la consola es otra pantalla y
          necesita su propia region `aria-live`, si no una asignacion guardada
          no anunciaria nada. */}
      <ToastHost />
    </div>
  );
}
